import React, { useEffect, useRef, useState } from 'react';
import { IoPerson } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import { FaCamera, FaStar, FaStarHalfAlt } from "react-icons/fa";
import { GiTeacher } from "react-icons/gi";
import axios from 'axios';
import { Config } from '../../API/config';
import { UserInfoData } from '../../types/userInfo';
import { useCookies } from 'react-cookie';
import toast, { Toaster } from 'react-hot-toast';
import { MdAddCircle } from 'react-icons/md';

const UserProfile = () => {
    const [user, setUser] = useState<UserInfoData | null>(null);
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const { id } = useParams();
    const [cookies, setCookie] = useCookies(['user-data', "token"]);
    const [role, setRole] = useState<string>("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [img, setImg] = useState<File | null>(null);

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        navigate("/newchat", { state: { Trainer: user } });
    };

    const fetchTrainerData = async () => {
        try {
            setLoading(true);
            let url = cookies["user-data"].id !== id ? `${Config.getUserInfoById}/${id}` : Config.getUserInfo;
            let { data } = await axios.get(url, { withCredentials: true });
            if (data) {
                setUser(data);
                setRole(data?.role);
            }
        } catch (err) {
            console.error("Error fetching trainer data:", err);
        } finally {
            setLoading(false);
        }
    };

    const addNewEnrollment = async () => {
        try {
            setLoading(true);
            let { data } = await axios.post(
                `${Config.addNewEnrollment}/${id}`,
                {},
                { withCredentials: true }
            );
            if (data.statusCode === 201) {
                toast.success("تم تسجيل طلب الحجز بنجاح");
            } else {
                toast.error(data.message || "حدث خطأ غير متوقع");
            }
        } catch (err: any) {
            console.error("Error adding enrollment:", err);
            if (err.response) {
                toast.error(err.response.data?.message || "حدث خطأ أثناء الحجز");
            } else {
                toast.error("تعذر الاتصال بالخادم. تحقق من اتصالك بالإنترنت.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!e.target.files || !e.target.files[0]) return;

            setLoading(true);
            const file = e.target.files[0];
            setImg(file);

            const formData = new FormData();
            formData.append('profileImage', file);

            const { data } = await axios.post(
                Config.updateProfileImage,
                formData,
                {
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            );

            if (data.statusCode === 201) {
                setUser(data.data);
                setRole(data?.data.role);
                const updatedUserData = {
                    ...cookies['user-data'],
                    profileImage: data.data.profileImage,
                };
                setCookie('user-data', updatedUserData, { path: '/' });
                toast.success("تم تحديث الصورة الشخصية بنجاح");
            }
        } catch (err) {
            console.error("Error updating profile image:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrainerData();
    }, []);

    const renderStars = (rating: number) => {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

        return (
            <div className="flex space-x-1">
                {Array(fullStars).fill(0).map((_, i) => (
                    <FaStar key={`full-${i}`} className="text-yellow-300" />
                ))}
                {hasHalfStar && <FaStarHalfAlt key="half" className="text-yellow-300" />}
                {Array(emptyStars).fill(0).map((_, i) => (
                    <FaStar key={`empty-${i}`} className="text-gray-300" />
                ))}
            </div>
        );
    };

    return (
        <div className="max-w-[80%] mx-auto p-6 bg-white shadow-lg rounded-lg text-right">
            <Toaster />
            {loading ? (
                <div className="text-center text-lg font-semibold text-gray-500">جاري التحميل...</div>
            ) : (
                <>
                    {/* Profile Image Section */}
                    <div className="flex flex-row-reverse items-center">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-md">
                            <FaCamera size={18} className="text-primary-200" />
                        </div>
                        <h2 className="mr-3 text-[20px] font-[400]">الصورة الشخصية</h2>
                    </div>

                    <div className="flex items-center space-x-4 mt-6 flex-row-reverse">
                        {user?._id === cookies["user-data"].id && user?.role === "trainee" ? (
                            <div className="w-24 h-24 flex justify-center items-center rounded-full overflow-hidden border border-gray-300 bg-gradient-to-b from-neutral-400 to-neutral-300">
                                {user?.profileImage ? (
                                    <img src={user.profileImage} alt="Trainer" className="w-full h-full object-cover" />
                                ) : (
                                    <IoPerson size={50} color="#8e8e8e" />
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="w-24 h-24 flex justify-center items-center rounded-full overflow-hidden border border-gray-300 bg-gradient-to-b from-neutral-400 to-neutral-300">
                                    {user?.profileImage ? (
                                        <img src={user.profileImage} alt="Trainer" className="w-full h-full object-cover" />
                                    ) : (
                                        <IoPerson size={50} color="#8e8e8e" />
                                    )}
                                </div>
                                {cookies["user-data"].id !== id && (
                                    <div className="flex flex-col space-y-2 p-5">
                                        <button onClick={handleButtonClick} className="bg-blue-500 text-white px-4 py-2 rounded-lg">ابعت رسالة</button>
                                        <button onClick={addNewEnrollment} className="bg-green-500 text-white px-4 py-2 rounded-lg">احجز الان</button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {user?.role === "trainer" && (
                        <div className="flex items-center mt-4 flex-row-reverse">
                            <span className="font-semibold text-lg ml-2">:التقييم</span>
                            {renderStars(user?.trainerId?.rate || 0)}
                        </div>
                    )}

                    {/* Personal Information Section */}
                    <div className="mt-8">
                        <div className="flex flex-row-reverse items-center mb-5">
                            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-md">
                                <IoPerson size={18} className="text-primary-200" />
                            </div>
                            <h2 className="mr-3 text-[20px] font-[400]">المعلومات الشخصية</h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-y-4 text-lg">
                            <p className='md:order-3'>
                                <span className="font-semibold">:الاسم</span>
                                <span className="block text-gray-700">{user?.name}</span>
                            </p>
                            <p className='md:order-2'>
                                <span className="font-semibold">:العمر</span>
                                <span className="block text-gray-700">{user?.trainerId?.age} سنة</span>
                            </p>
                            <p className='md:order-1'>
                                <span className="font-semibold">:الجنس</span>
                                <span className="block text-gray-700">{user?.gender}</span>
                            </p>
                        </div>
                        <div className="grid md:grid-cols-3 gap-y-4 text-lg mt-4">
                            <p className='md:order-3'>
                                <span className="font-semibold">:الموقع</span>
                                <span className="block text-gray-700">{user?.governorate}, {user?.city}</span>
                            </p>
                            {cookies['user-data'].id === user?._id ? (
                                <p className='md:order-2'>
                                    <span className="font-semibold">:رقم الهاتف</span>
                                    <span className="block text-gray-700">{user?.phone || "غير متوفر"}</span>
                                </p>
                            ) : (<p className='md:order-2'></p>)}
                            <p className='md:order-1'></p>
                        </div>
                    </div>

                    {/* Trainer Information Section */}
                    {role === "trainer" && (
                        <div className="mt-8">
                            <div className="flex flex-row-reverse items-center mb-5">
                                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-md">
                                    <GiTeacher size={18} className="text-primary-200" />
                                </div>
                                <h2 className="mr-3 text-[20px] font-[400]">معلومات المدرب</h2>
                            </div>
                            <div className="grid md:grid-cols-3 gap-y-4 text-lg">
                                <p>
                                    <span className="font-semibold">:اسم الفئة</span>
                                    <span className="block text-gray-700">{user?.trainerId?.category}</span>
                                </p>
                                <p>
                                    <span className="font-semibold">:سعر الحصة</span>
                                    <span className="block text-gray-700">{user?.trainerId?.priceOfLesson}</span>
                                </p>
                                <p>
                                    <span className="font-semibold">:الحصص الأسبوعية</span>
                                    <span className="block text-gray-700">{user?.trainerId?.numberOfLessonsOnWeek}</span>
                                </p>
                            </div>
                            <div className="grid md:grid-cols-3 gap-y-4 text-lg mt-10">
                                <p>
                                    <span className="font-semibold">:اسم الفئة الفرعية</span>
                                    <span className="block text-gray-700">{user?.trainerId?.subCategory}</span>
                                </p>
                                <p className="md:col-span-1">
                                    <span className="font-semibold">:إجمالي الحصص</span>
                                    <span className="block text-gray-700">{user?.trainerId?.numOfOrders}</span>
                                </p>
                                <p className="md:col-span-1">
                                    <span className="font-semibold block mb-1">اين</span>
                                    {user?.trainerId?.serviceCities ? (
                                        <div className="flex flex-wrap gap-2 justify-end">
                                            {user.trainerId?.serviceCities.map((place, index) => {
                                                const cleanedPlace = String(place).replace(/[\[\]"]+/g, '').trim();
                                                const placeArray = cleanedPlace.split(',').map(item => item.trim());
                                                return placeArray.map((splitPlace, splitIndex) => (
                                                    <div
                                                        key={`${index}-${splitIndex}`}
                                                        className="p-1 px-2 border border-primary-200 rounded-lg bg-primary-50 text-primary-700 text-sm max-w-[150px] truncate"
                                                    >
                                                        {splitPlace}
                                                    </div>
                                                ));
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-gray-700">لا توجد مواقع متاحة حاليًا</p>
                                    )}
                                </p>
                                <p className="md:col-span-2">
                                    <span className="font-semibold">:سنوات الخبرة</span>
                                    <span className="block text-gray-700">{user?.trainerId?.yearsOfExperience || "غير محدد"}</span>
                                </p>
                                <p className="md:col-span-1">
                                    <span className="font-semibold">:ملاحظات</span>
                                    <span className="block text-gray-700">{user?.trainerId?.notice || "لا توجد ملاحظات"}</span>
                                </p>
                                <p className="md:col-span-3">
                                    <span className="font-semibold">:احتياجات الكورس</span>
                                    <span className="block text-gray-700">{user?.trainerId?.needs || "لا توجد احتياجات محددة"}</span>
                                </p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default UserProfile;